import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { fetchMyBookings, fetchMyTutors, fetchTutors, fetchSubjects } from '../services/api';
import { useAuth } from '../components/AuthContext';
import { useInstitution } from '../components/InstitutionContext';

const BookLessonPage = () => {
  const { user } = useAuth();
  const { selectedInstitution } = useInstitution();
  const [bookings, setBookings] = useState([]);
  const [myTutors, setMyTutors] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filters, setFilters] = useState({ subject: '', minPrice: '', maxPrice: '' });

  useEffect(() => {
    const stored = localStorage.getItem('lessonFilters');
    if (stored) setFilters(JSON.parse(stored));
    const load = async () => {
      try {
        const [b, t, s] = await Promise.all([
          fetchMyBookings(),
          fetchMyTutors(),
          fetchSubjects(),
        ]);
        setBookings(b);
        setMyTutors(t);
        setSubjects(s);
      } catch (err) {
        console.error('Failed to load data', err);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const institutionId =
      user?.userprofile?.institution?.id || selectedInstitution;
    if (!institutionId) return;
    const loadTutors = async () => {
      try {
        const data = await fetchTutors(institutionId, {
          subject: filters.subject,
          min_price: filters.minPrice,
          max_price: filters.maxPrice,
        });
        setTutors(data);
      } catch (err) {
        console.error('Failed to load tutors', err);
      }
    };
    loadTutors();
  }, [filters, user, selectedInstitution]);

  const handleFilterChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveFilters = () => {
    localStorage.setItem('lessonFilters', JSON.stringify(filters));
  };

  const now = new Date();
  const upcoming = bookings.filter(
    (b) => new Date(b.start_time) >= now,
  );
  const past = bookings.filter((b) => new Date(b.start_time) < now);

  return (
    <Box sx={{ px: 4, py: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        The Tutoring page is under active development, please stay tuned, and
        feel free to explore, and give us suggestions through the{' '}
        <Link component={RouterLink} to="/feedback">
          feedback
        </Link>{' '}
        page
      </Alert>
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" gutterBottom color="secondary">
            My Bookings
          </Typography>
        <List>
          {upcoming.map((b) => (
            <ListItem key={b.id} divider>
              <ListItemText
                primary={`${b.title} with ${b.tutor}`}
                secondary={`Starts ${new Date(b.start_time).toLocaleString()}`}
              />
            </ListItem>
          ))}
          {upcoming.length === 0 && (
            <ListItem>
              <ListItemText primary="No upcoming sessions" />
            </ListItem>
          )}
        </List>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h5" gutterBottom color="secondary">
          Past Sessions
        </Typography>
        <List>
          {past.map((b) => (
            <ListItem key={b.id} divider>
              <ListItemText
                primary={`${b.title} with ${b.tutor}`}
                secondary={`Started ${new Date(b.start_time).toLocaleString()}`}
              />
            </ListItem>
          ))}
          {past.length === 0 && (
            <ListItem>
              <ListItemText primary="No past sessions" />
            </ListItem>
          )}
        </List>
        <Divider sx={{ my: 4 }} />
        <Typography variant="h4" gutterBottom color="secondary">
          My Tutors
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {myTutors.map((t) => (
            <Card key={t.id} sx={{ width: 250 }}>
              <CardContent>
                <Typography variant="h6">{t.full_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t.subjects.map((s) => s.name).join(', ')}
                </Typography>
              </CardContent>
            </Card>
          ))}
          {myTutors.length === 0 && (
            <Typography>No tutors booked yet.</Typography>
          )}
        </Box>
        <Divider sx={{ my: 4 }} />
        <Typography variant="h4" gutterBottom color="secondary">
          Tutor Results
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {tutors.map((t) => (
            <Card key={t.id} sx={{ width: 250 }}>
              <CardContent>
                <Typography variant="h6">{t.full_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t.subjects.map((s) => s.name).join(', ')}
                </Typography>
              </CardContent>
            </Card>
          ))}
          {tutors.length === 0 && (
            <Typography>No tutors found.</Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ width: 300 }}>
        <Typography variant="h5" gutterBottom color="secondary">
          Find a Tutor
        </Typography>
        <FormControl fullWidth color="secondary" sx={{ mb: 2 }}>
          <InputLabel id="subject-label" color="secondary">Subject</InputLabel>
          <Select
            labelId="subject-label"
            value={filters.subject}
            label="Subject"
            onChange={handleFilterChange('subject')}
            color="secondary"
          >
            <MenuItem value="">
              <em>Any</em>
            </MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Min Price"
          type="number"
          value={filters.minPrice}
          onChange={handleFilterChange('minPrice')}
          fullWidth
          color="secondary"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Max Price"
          type="number"
          value={filters.maxPrice}
          onChange={handleFilterChange('maxPrice')}
          fullWidth
          color="secondary"
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSaveFilters}
          fullWidth
        >
          Save Filters
        </Button>
      </Box>
    </Box>
    </Box>
  );
};

export default BookLessonPage;
